import {values, map, compact, fromPairs, mapValues, pickBy} from 'lodash';
import concatMapboxStyles from '../../mapbox/concatMapboxStyles';
import opacifyMapboxStyle from '../../mapbox/opacifyMapboxStyle';
import {blurOpacity} from './constants';
import {areaFixedLayerIds} from './fixedLayerIds';

type LocalFixedLayerIds = typeof areaFixedLayerIds['localUser'];
type RemoteFixedLayerIds = typeof areaFixedLayerIds['remoteUser'];

interface GetAreasMapboxStyleOptions {
    highlight: MapHighlight;
    interaction: MapInteraction;
    isPrinting: boolean;
    showAreas: boolean;
    areas: {[id: string]: Area};
    foldersMembership: MapFoldersMembership;
    user: User | undefined;
}

export default function getStyle(options: GetAreasMapboxStyleOptions) {
    return {
        metadata: {
            uniqueTilecacheKey: 'areas'
        },
        ...concatMapboxStyles(compact([
            opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : blurOpacity) : 1,
                getRemoteUserStyle(areaFixedLayerIds.remoteUser, options)
            ),
            opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : blurOpacity) : 1,
                getLocalUserStyle(areaFixedLayerIds.localUser, options)
            ),
            getRemoteHighlightStyle(areaFixedLayerIds.remoteHighlight, options),
            getLocalHighlightStyle(areaFixedLayerIds.localHighlight, options),
        ])),
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
    };
}


function getLocalUserStyle(layerIds: LocalFixedLayerIds, {
    interaction,
    showAreas,
    areas,
}: GetAreasMapboxStyleOptions): mapboxgl.Style | undefined {
    if (!showAreas) return;

    const features = compact(values(areas).filter((area: Area) => (
        !area.properties.isLocallyDeleted && area.isGeometryLoaded && area.unmodifiedArea
    )).map((area: Area) => area.isGeometryLoaded && area.unmodifiedArea));

    if (!features.length) return;

    const colorExpression: mapboxgl.Expression = [
        'get',
        ['get', 'areaLocalId'],
        ['literal', mapValues(areas, area => area.properties.color)]
    ];

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'localUser'
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features
                },
            }
        },
        layers: [
            getFillLayer({id: layerIds.fill, interaction, paint: {'fill-color': colorExpression}}),
            getLineLayer({id: layerIds.line, interaction, paint: {'line-color': colorExpression}}),
        ]
    };
}


function getRemoteUserStyle(layerIds: RemoteFixedLayerIds, {
    interaction,
    showAreas,
    areas,
    highlight,
    user,
}: GetAreasMapboxStyleOptions): mapboxgl.Style | undefined {
    if (!user || !user.isAuthenticated || !showAreas) return;

    const filter = [
        'all',
        getRemoteFilter(areas),
        highlight && highlight.type === 'area' ?
            ['!=', ['get', 'id'], highlight.id] :
            ['literal', true]
    ];

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'remoteUser'
        },
        sources: {
            source: {
                type: 'vector',
                tiles: [`${window.location.origin}/api/tiles/areas/{z}/{x}/{y}.mvt`],
                minzoom: 3,
                maxzoom: 14
            }
        },
        layers: [
            getFillLayer({id: layerIds.fill, interaction, filter, 'source-layer': 'areas'}),
            getLineLayer({id: layerIds.line, interaction, filter, 'source-layer': 'areas'}),
        ]
    };
}

function getRemoteHighlightStyle(layerIds: RemoteFixedLayerIds, {
    areas,
    interaction,
    highlight,
}: GetAreasMapboxStyleOptions): mapboxgl.Style | undefined {

    const source = getRemoteHighlightSource(highlight);
    if (!source) return;

    const filter = getRemoteFilter(areas);

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'remoteHighlight'
        },
        sources: {source},
        layers: [
            getFillLayer({
                id: layerIds.fill,
                interaction,
                filter,
                ...source.type === 'vector' && {'source-layer': 'areas'},
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                filter,
                ...source.type === 'vector' && {'source-layer': 'areas'},
            }),
        ]
    };
}

function getLocalHighlightStyle(layerIds: LocalFixedLayerIds, {
    interaction,
    areas,
    highlight,
    foldersMembership,
}: GetAreasMapboxStyleOptions): mapboxgl.Style | undefined {

    const features = compact(values(areas).filter((area: Area) => (
        !area.properties.isLocallyDeleted && isHighlighted(highlight, area, foldersMembership)
    )).map((area: Area) => area.isGeometryLoaded && area.unmodifiedArea));

    if (!features.length) return;

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'localHighlight'
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            }
        },
        layers: [
            getFillLayer({id: layerIds.fill, interaction}),
            getLineLayer({id: layerIds.line, interaction}),
        ]
    };
}

function isHighlighted(
    highlight: MapHighlight,
    area: Area,
    foldersMembership: MapFoldersMembership
) {
    if (!highlight) {
        return false;
    } else if (highlight.type === 'area') {
        return highlight.id === area.properties.areaLocalId;
    } else if (highlight.type === 'folder') {
        const folder = foldersMembership[highlight.id];
        return folder && folder.tracks[area.properties.areaLocalId];
    } else {
        return false;
    }
}

interface GetLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: MapInteraction;
    id: string;
}

function getFillLayer({interaction, paint, ...layer}: GetLayerOptions): mapboxgl.Layer {
    return {
        type: 'fill',
        source: 'source',
        minzoom: 3,
        paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': getFocusExpression(interaction, blurOpacity, 0.2),
            ...paint,
        },
        ...layer,
    };
}

function getLineLayer({interaction, paint, layout, ...layer}: GetLayerOptions): mapboxgl.Layer {
    return {
        type: 'line',
        source: 'source',
        paint: {
            'line-color': ['get', 'color'],
            'line-width': getFocusExpression(interaction, 2, 2),
            'line-opacity': getFocusExpression(interaction, blurOpacity, 1),
            ...paint,
        },
        layout: {'line-cap': 'round', ...layout},
        ...layer
    };
}

function getRemoteFilter(areas: Record<string, Area>) {
    return [
        'all',
        [
            'has',
            ['geometry-type'],
            ['literal', {Polygon: true, MultiPolygon: true}]
        ],
        ['!', ['has', ['get', 'id'], ['literal', fromPairs(
            map(
                pickBy(areas, area => area.isGeometryLoaded),
                area =>[area.properties.id, true]
            )
        )]]],
    ];
}

function getFocusExpression<T>(
    interaction: MapInteraction, blurred: T, normal: T
): T | mapboxgl.Expression {
    return interaction.type === 'editingRoute' ? blurred : normal;
}


function getRemoteHighlightSource(
    highlight: MapHighlight
): mapboxgl.AnySourceData | undefined {
    if (!highlight || (highlight.type !== 'area' && highlight.type !== 'folder')) return;

    const pubLink = highlight.pubLink ? `?pubLink=${highlight.pubLink}` : '';

    if (highlight.type === 'area') {
        return {
            type: 'geojson',
            data: `${window.location.origin}/api/objects/area/${highlight.id}/${pubLink}`
        };

    } else if (highlight.type === 'folder') {
        return {
            type: 'vector',
            // eslint-disable-next-line max-len
            tiles: [`${window.location.origin}/api/tiles/folders/${highlight.id}/{z}/{x}/{y}.mvt${pubLink}`]
        };
    }
}
